import React, { useState } from "react";
import { useUserPrompt } from "hooks/useUserPrompt";
import { DiscoveryJourneyModal } from "packages/membership/components/DiscoveryJourney/DiscoveryJourneyModal";
import { AnniversaryGiftTeaser } from "packages/membership/components/AnniversaryGiftTeaser/AnniversaryGiftTeaser";
import { NotificationComponent } from "packages/membership/components/Notification/NotificationComponent";
import { notificationComponentWrapper } from "packages/membership/components/AnniversaryGiftTeaser/AnniversaryGiftTeaser.css";

const VISIBILITY = {
  DISCOVERY_JOURNEY_MODAL: {
    prompt: "DISCOVERY_JOURNEY_MODAL",
    featureFlag: {
      name: "experiment-use-discovery-journey",
      requiredValue: "1"
    }
  },
  ANNIVERSARY_GIFT_TEASER: {
    prompt: "ANNIVERSARY_GIFT_TEASER"
  }
};

const CTA_LABELS = {
  EMAIL_ME_WHEN_ITS_HERE: "Email me when it's here",
  CLOSE: "close"
};

export const UserPromptModal: React.FC = () => {
  const [activeCTALabel, setActiveCTALabel] = useState<string>("");
  const {
    shouldShowPrompt,
    markPromptAsSeen,
    showNotification,
    setShowNotification
  } = useUserPrompt();

  // Decide which modal to show
  const showDiscoveryJourneyPrompt = shouldShowPrompt(
    VISIBILITY.DISCOVERY_JOURNEY_MODAL.prompt,
    VISIBILITY.DISCOVERY_JOURNEY_MODAL.featureFlag
  );

  const DiscoveryJourneyModalComponent = showDiscoveryJourneyPrompt ? (
    <DiscoveryJourneyModal
      data-testid="DiscoveryJourneyModal"
      isVisible
      setNextVisibility={(visible: boolean, next?: () => void) => {
        if (visible === false)
          markPromptAsSeen(VISIBILITY.DISCOVERY_JOURNEY_MODAL.prompt).finally(
            () => next?.()
          );
      }}
    />
  ) : null;

  const showAnniversaryGiftTeaserPrompt = shouldShowPrompt(
    VISIBILITY.ANNIVERSARY_GIFT_TEASER.prompt
  );

  if (showAnniversaryGiftTeaserPrompt) {
    return (
      <AnniversaryGiftTeaser
        data-testid="AnniversaryGiftTeaser"
        isVisible
        setNextVisibility={(visible: boolean, next?: () => void) => {
          if (visible === false) {
            const promptsRead = [VISIBILITY.ANNIVERSARY_GIFT_TEASER.prompt];
            if (showDiscoveryJourneyPrompt) {
              promptsRead.push(VISIBILITY.DISCOVERY_JOURNEY_MODAL.prompt);
            }
            markPromptAsSeen(...promptsRead).finally(() => next?.());
          }
        }}
        ctaLabels={CTA_LABELS}
        setActiveCTALabel={setActiveCTALabel}
      >
        {DiscoveryJourneyModalComponent}
      </AnniversaryGiftTeaser>
    );
  } else if (
    showNotification &&
    activeCTALabel === CTA_LABELS.EMAIL_ME_WHEN_ITS_HERE // `Notification` component should be displayed only if the user clicks on `Email me when it's here CTA` in `Anniversary Gift Teaser` modal
  ) {
    return (
      <div className={notificationComponentWrapper}>
        <NotificationComponent
          visible={showNotification}
          setIsVisible={(newValue) => setShowNotification?.(newValue)}
          title="We'll email you a reminder"
          duration={5000}
        />
      </div>
    );
  }

  return DiscoveryJourneyModalComponent;
};
